// Local Storage Service
// Handles saving and loading game state from browser localStorage

const STORAGE_KEY_PREFIX = 'axes2_game_';

export const storageService = {
    /**
     * Save a game to localStorage
     * @param {Object} gameData - The game data to save
     * @returns {string} The GUID of the saved game
     */
    saveGame(gameData) {
        try {
            const guid = gameData.guid || this.generateGuid();
            const storageKey = `${STORAGE_KEY_PREFIX}${guid}`;

            const gameToSave = {
                guid,
                title: gameData.title,
                gameType: gameData.gameType,
                dateTimeStamp: new Date().toISOString(),
                data: gameData
            };

            localStorage.setItem(storageKey, JSON.stringify(gameToSave));
            return guid;
        } catch (error) {
            console.error('Error saving game to localStorage:', error);
            throw new Error('Failed to save game');
        }
    },

    /**
     * Load a game from localStorage by GUID
     * @param {string} guid - The game GUID
     * @returns {Object|null} The game data or null if not found
     */
    loadGame(guid) {
        try {
            const storageKey = `${STORAGE_KEY_PREFIX}${guid}`;
            const savedData = localStorage.getItem(storageKey);

            if (!savedData) {
                return null;
            }

            const parsed = JSON.parse(savedData);
            return parsed.data;
        } catch (error) {
            console.error('Error loading game from localStorage:', error);
            return null;
        }
    },

    /**
     * Delete a game from localStorage
     * @param {string} guid - The game GUID
     * @returns {boolean} Success status
     */
    deleteGame(guid) {
        try {
            const storageKey = `${STORAGE_KEY_PREFIX}${guid}`;
            localStorage.removeItem(storageKey);
            return true;
        } catch (error) {
            console.error('Error deleting game from localStorage:', error);
            return false;
        }
    },

    /**
     * Get all saved games from localStorage
     * @returns {Array} Array of saved game metadata
     */
    listSavedGames() {
        try {
            const savedGames = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);

                if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
                    const data = localStorage.getItem(key);
                    if (data) {
                        try {
                            const parsed = JSON.parse(data);
                            savedGames.push({
                                Guid: parsed.guid,
                                title: parsed.title,
                                gameType: parsed.gameType,
                                dateTimeStamp: parsed.dateTimeStamp
                            });
                        } catch (parseError) {
                            console.warn(`Failed to parse saved game ${key}:`, parseError);
                        }
                    }
                }
            }

            // Sort by date, newest first
            savedGames.sort((a, b) => new Date(b.dateTimeStamp) - new Date(a.dateTimeStamp));

            return savedGames;
        } catch (error) {
            console.error('Error listing saved games:', error);
            return [];
        }
    },

    /**
     * Clear all saved games
     * @returns {number} Number of games cleared
     */
    clearAllGames() {
        try {
            const keys = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
                    keys.push(key);
                }
            }

            keys.forEach(key => localStorage.removeItem(key));
            return keys.length;
        } catch (error) {
            console.error('Error clearing all games:', error);
            return 0;
        }
    },

    /**
     * Generate a simple GUID (fallback if uuid not available)
     * @returns {string} A GUID string
     */
    generateGuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
};
